import { isBytesTag } from './dbCellTags';
import { formatJson } from '../utils/formatCode';
import { formatXml } from '../utils/xmlFormat';

/**
 * How the Value Panel decides to PRESENT a cell — kept out of the component so
 * it can be exercised without React (same split as dbGridSql/dbExportUtils).
 */

/** Longest value the panel renders into an editor; beyond this it goes read-only. */
export const LARGE_VALUE_CHARS = 1024 * 1024;

/**
 * The view mode to open a value in, before any user override.
 *
 * The column's declared type is authoritative where it says anything useful.
 * Failing that the content is sniffed, because a JSON document living in a
 * plain varchar column is common enough that opening it as flat text is the
 * wrong default — and it is precisely the case a type-only rule misses.
 */
export function detectViewMode(value, columnType) {
  if (isBytesTag(value)) return 'binary';
  const type = String(columnType || '').toLowerCase();
  if (/json/.test(type)) return 'json';
  if (/xml/.test(type)) return 'xml';
  if (/bytea|blob|binary/.test(type)) return 'binary';

  const text = typeof value === 'string' ? value.trim() : '';
  if (text.startsWith('{') || text.startsWith('[')) {
    try {
      JSON.parse(text);
      return 'json';
    } catch {
      // Starts like JSON but isn't. Offering to "format" it would mangle it.
    }
  }
  if (text.startsWith('<?xml') || (text.startsWith('<') && text.endsWith('>'))) return 'xml';
  return 'text';
}

/**
 * Pretty-print for modes with a canonical shape. Invalid input is returned
 * untouched: this runs while editing, so it must never destroy in-progress text.
 */
export function formatForMode(text, mode) {
  if (mode === 'json') {
    try {
      return formatJson(text); // a bigint / numeric column value keeps every digit
    } catch {
      return text;
    }
  }
  // XML had no branch here, so Format did nothing for it (found in set 12).
  if (mode === 'xml') return formatXml(text) ?? text;
  return text;
}

/**
 * base64 → the `00 01 02` grouping a hex viewer shows, 16 bytes per line.
 * Truncated with a remaining-byte count rather than rendering a whole blob into
 * the DOM.
 */
export function base64ToHexDump(b64, maxBytes = 4096) {
  let bin;
  try {
    bin = atob(b64);
  } catch {
    return '';
  }
  const shown = Math.min(bin.length, maxBytes);
  const bytes = [];
  for (let i = 0; i < shown; i += 1) {
    bytes.push(bin.charCodeAt(i).toString(16).padStart(2, '0'));
  }
  const lines = [];
  for (let i = 0; i < bytes.length; i += 16) {
    lines.push(bytes.slice(i, i + 16).join(' '));
  }
  const body = lines.join('\n');
  return shown < bin.length ? `${body}\n… ${bin.length - shown} more bytes` : body;
}
